import type { FulfillmentOrderLine } from "../types";
import type { EntryExistingDraft, EntryParseBaseInput, EntryParseResult } from "./confirmationEntryTypes";
import { prepareExistingBusEntry, parseExistingBusEntry } from "./confirmationExistingBusEntry";
import { parseBusEntry } from "./confirmationBusEntryService";
import { normalized } from "./confirmationEntryPrimitives";
import { changedEntryFields } from "./confirmationEntryChange";
import { parseConfirmationDayExpression } from "./confirmationEntryParser";

const compact = (text: string) => normalized(text).replace(/\s+/g, "");
const identity = (text: string) => {
  const match = normalized(text).match(/^([dD\d,-]+)\s+(?:(当地用车|包车|单接|单送|接送|原段合计)\s+)?@(\d+座)/);
  if (!match) return compact(text.match(/^(.*?)\s+@.*?座/)?.[0] || text);
  return JSON.stringify([parseConfirmationDayExpression(match[1])?.numbers || match[1], !match[2] || match[2] === "包车" ? "当地用车" : match[2], match[3]]);
};

/** 主体全文按原区段、用途、车型匹配原 ID；同身份多笔发生变化时拒绝猜测。 */
export function parseBusGroupDraft(input: EntryParseBaseInput, originals: FulfillmentOrderLine[]): EntryParseResult & { drafts: EntryExistingDraft[]; locations: Record<string, { sourceStart: number; sourceEnd: number }> } {
  const seeds = originals.map(base => prepareExistingBusEntry(base, input.routeDays));
  const sources = [...input.text.matchAll(/[^\n;；]+/g)].filter(match => match[0].trim()).map(match => ({ raw: match[0], sourceStart: match.index, sourceEnd: match.index + match[0].length }));
  const drafts = originals.map(base => ({ base, patch: {}, removed: false } as EntryExistingDraft));
  const result: ReturnType<typeof parseBusGroupDraft> = { items: [], errors: [], drafts, locations: {} };
  const matches = new Map<number, number>();
  const claimed = new Set<number>();
  sources.forEach((source, index) => {
    const seedIndex = seeds.findIndex((seed, i) => !claimed.has(i) && identity(seed.input.text) === identity(source.raw));
    if (seedIndex >= 0) { matches.set(index, seedIndex); claimed.add(seedIndex); }
  });
  const remainingSeeds = seeds.map((_, index) => index).filter(index => !claimed.has(index));
  const remainingSources = sources.map((_, index) => index).filter(index => !matches.has(index));
  if (remainingSeeds.length === 1 && remainingSources.length === 1) matches.set(remainingSources[0], remainingSeeds[0]);
  else if (remainingSeeds.length && remainingSources.length) result.errors.push({ raw: input.text, sourceStart: 0, sourceEnd: input.text.length, field: "formula", message: "多笔原费用的范围或车型同时变化，无法确认原行；请只勾选目标费用重新编辑，避免丢失原身份" });
  const used = new Set<number>();
  for (const [sourceIndex, source] of sources.entries()) {
    const related = seeds.map((seed, index) => ({ seed, index })).filter(({ seed }) => identity(seed.input.text) === identity(source.raw));
    const sameSources = sources.filter(item => identity(item.raw) === identity(source.raw));
    if (related.length > 1 && (sameSources.length !== related.length || sameSources.some((item, index) => compact(item.raw) !== compact(related[index].seed.input.text)))) {
      result.errors.push({ ...source, field: "formula", message: "同区段、用途和车型存在多笔费用，无法唯一确认原行；请只勾选目标费用重新编辑，避免互换主体与历史金额" });
      continue;
    }
    const matchedIndex = matches.get(sourceIndex);
    const matched = matchedIndex === undefined ? undefined : { seed: seeds[matchedIndex], index: matchedIndex };
    if (matched) {
      const { seed, index } = matched;
      used.add(index); result.locations[seed.base.id] = source;
      // 未改文本不强迫历史缺资源、缺日期费用重新解析；两类主体仍只按显式选择更新。
      if (compact(source.raw) === compact(seed.input.text)) {
        const provider = input.provider && "id" in input.provider ? input.provider : null;
        const patch: Partial<FulfillmentOrderLine> = {};
        const previousProvider = seed.input.provider && "id" in seed.input.provider ? seed.input.provider : null;
        if ((provider?.id ?? null) !== (previousProvider?.id ?? null)) Object.assign(patch, { fulfillmentSupplierId: provider?.id ?? null, fulfillmentSupplierName: provider?.name ?? null });
        if (!seed.base.fulfillmentOrderId && input.settlement !== undefined && (input.settlement?.id ?? null) !== (seed.input.settlement?.id ?? null)) Object.assign(patch, { settlementSupplierId: input.settlement?.id ?? null, settlementSupplierName: input.settlement?.name ?? null });
        drafts[index].patch = changedEntryFields({ base: seed.base, patch, removed: false });
      } else {
        const parsed = parseExistingBusEntry(seed, { ...input, text: source.raw, settlement: input.settlement === undefined ? seed.input.settlement : input.settlement });
        drafts[index].patch = parsed.patch;
        result.errors.push(...parsed.errors.map(error => ({ ...error, ...source })));
      }
    } else {
      if (source.raw.includes("原段合计")) {
        result.errors.push({ ...source, field: "formula", message: "原段合计仅用于本次已有接送费用，请保留原范围与车型；不能复制成新费用" });
        continue;
      }
      const parsed = parseBusEntry({ ...input, text: source.raw });
      result.items.push(...parsed.items.map((item, index) => ({ ...item, ...source, key: `${source.sourceStart}-${index}` })));
      result.errors.push(...parsed.errors.map(error => ({ ...error, ...source })));
    }
  }
  if (!result.errors.length) drafts.forEach((draft, index) => { draft.removed = !used.has(index); });
  return result;
}
